import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Redirect, useHistory } from 'react-router-dom';
import { canModifyContent } from '@/lib/account-roles';
import {
  buildFestivalLocationPayload,
  isLocationNameSet,
  LOCATION_NAME_REQUIRED_MESSAGE,
} from '@/lib/festival-location';
import { hasLocationCoordinates } from '@/lib/google-maps';
import { INVALID_DATE_MESSAGE, isInvalidCompleteDate } from '@/lib/date-mask';
import { scrollToFirstFormError } from '@/lib/scroll-to-form-error';
import { withServerDateFieldsList } from '@/lib/server-date';
import { useAuthStore } from '@/stores/auth-store';
import { useFestivalWorkspaceStore } from '@/stores/festival-workspace-store';
import {
  useChangeFestivalStatusMutation,
  useSaveFestivalMutation,
} from '../../queries/festivals/mutations';
import { useFestivalDetailQuery } from '../../queries/useFestivalDetailQuery';
import {
  Button,
  DateFormWidget,
  FestivalHeader,
  FileUpload,
  FormRow,
  Loading,
  LocationMapField,
} from '../controls';
import { FestivalEventStatusPanel } from './FestivalEventStatusPanel';
import { FestivalQrCodesSidebar } from './FestivalPromotionImages';
import { FestivalRelatedContent } from './FestivalRelatedContent';

const defaultDateObject = {
  startTime: null,
  endTime: null,
  startTimeMS: null,
  endTimeMS: null,
  selectedDate: null,
  dateInput: null,
};

const defaultLinks = {
  instagram: '',
  twitter: '',
  facebook: '',
  web: '',
};

const EDIT_AUTOSAVE_DEBOUNCE_MS = 2000;

function buildAutosaveSnapshot(values, festival) {
  return JSON.stringify({
    name: values?.name?.trim() ?? '',
    description: values?.description?.trim() ?? '',
    locationName: values?.locationName ?? '',
    links: values?.links ?? defaultLinks,
    dates: getCompleteDates(festival?.dates),
    location: {
      latitude: festival?.location?.latitude ?? null,
      longitude: festival?.location?.longitude ?? null,
      locationName: festival?.location?.locationName ?? '',
      radius: festival?.location?.radius ?? null,
    },
    photo: festival?.photo ?? null,
    photoURL: festival?.photoURL ?? null,
  });
}

function getDatesValidationError(dates) {
  if (!dates?.length) {
    return 'At least one date is required.';
  }

  const invalidDateRow = dates.find((dateObj) =>
    isInvalidCompleteDate(dateObj.dateInput),
  );
  if (invalidDateRow) {
    return INVALID_DATE_MESSAGE;
  }

  const filledRows = dates.filter(
    (dateObj) =>
      dateObj.startTimeMS != null ||
      dateObj.endTimeMS != null ||
      Boolean(dateObj.selectedDate) ||
      Boolean(dateObj.dateInput),
  );
  if (filledRows.length === 0) {
    return 'At least one date is required.';
  }

  const incompleteRow = filledRows.find(
    (dateObj) => !dateObj.startTimeMS || !dateObj.endTimeMS,
  );
  if (incompleteRow) {
    return 'Each date must include a date, start time, and end time.';
  }

  return null;
}

function getCompleteDates(dates) {
  return (dates ?? []).filter(
    (dateObj) => dateObj.startTimeMS != null && dateObj.endTimeMS != null,
  );
}

function locationIsSet(location) {
  return hasLocationCoordinates(location);
}

function computeWorkspaceErrors(festival, locationName) {
  const nextWorkspaceErrors = {};
  const datesError = getDatesValidationError(festival.dates);
  if (datesError) {
    nextWorkspaceErrors.dates = datesError;
  }
  if (!locationIsSet(festival.location)) {
    nextWorkspaceErrors.location =
      'A location is required. Enter an address or drag the pin onto the map.';
  }
  if (!isLocationNameSet(locationName)) {
    nextWorkspaceErrors.locationName = LOCATION_NAME_REQUIRED_MESSAGE;
  }
  return nextWorkspaceErrors;
}

function buildFormValues(festival) {
  return {
    name: festival.name || '',
    description: festival.description || '',
    locationName: festival.location?.locationName ?? '',
    links: { ...defaultLinks, ...(festival.links || {}) },
  };
}

function FestivalFormFields({
  festival,
  patchFestival,
  saveMutation,
  statusMutation,
  loading,
  bridgeLoading,
  readOnly = false,
  onNewFestivalSaved,
}) {
  const history = useHistory();
  const [workspaceErrors, setWorkspaceErrors] = useState({});
  const formRef = useRef(null);
  const festivalRef = useRef(festival);
  const lastSavedSnapshotRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const skipAutosaveRef = useRef(true);
  const saveInFlightRef = useRef(false);

  festivalRef.current = festival;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: buildFormValues(festival),
    mode: 'onSubmit',
  });

  const watchedName = watch('name');
  const watchedDescription = watch('description');
  const watchedLocationName = watch('locationName');
  const watchedLinks = watch('links');
  const isEditAutosave = Boolean(festival.festivalId) && !readOnly;

  useEffect(() => {
    reset(buildFormValues(festival));
  }, [festival.festivalId, reset]);

  useEffect(() => {
    skipAutosaveRef.current = true;
    lastSavedSnapshotRef.current = null;
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    const readyTimer = setTimeout(() => {
      lastSavedSnapshotRef.current = buildAutosaveSnapshot(
        getValues(),
        festivalRef.current,
      );
      skipAutosaveRef.current = false;
    }, 0);

    return () => {
      clearTimeout(readyTimer);
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [festival.festivalId, getValues]);

  const getScrollRoot = useCallback(() => {
    return (
      formRef.current?.closest('[data-modal-scroll]') ??
      formRef.current ??
      document
    );
  }, []);

  const scrollToFirstError = useCallback(
    (rhfErrors = {}, workspace = workspaceErrors) => {
      scrollToFirstFormError(getScrollRoot(), [
        { hasError: Boolean(rhfErrors.name), selector: '[name="name"]' },
        {
          hasError: Boolean(workspace.dates),
          selector: '[data-form-field="dates"]',
        },
        {
          hasError: Boolean(workspace.location || workspace.locationName),
          selector: '[data-form-field="location"]',
        },
        {
          hasError: Boolean(rhfErrors.description),
          selector: '[name="description"]',
        },
      ]);
    },
    [getScrollRoot, workspaceErrors],
  );
  const scrollToFirstErrorRef = useRef(scrollToFirstError);
  scrollToFirstErrorRef.current = scrollToFirstError;

  const handleDateChange = useCallback(
    (dates) => {
      patchFestival((current) => ({ ...current, dates }));
      setWorkspaceErrors((prev) => {
        const next = { ...prev };
        delete next.dates;
        return next;
      });
    },
    [patchFestival],
  );

  const handleLocationChange = useCallback(
    (locationUpdate, displayName) => {
      patchFestival((current) => ({
        location: {
          ...(current.location ?? {}),
          ...locationUpdate,
          ...(displayName != null ? { locationName: displayName } : null),
          radius: locationUpdate.radius ?? current.location?.radius ?? 1000,
        },
      }));
      if (displayName != null) {
        setValue('locationName', displayName, { shouldDirty: true });
      }
      setWorkspaceErrors((prev) => {
        const next = { ...prev };
        delete next.location;
        if (isLocationNameSet(displayName)) {
          delete next.locationName;
        }
        return next;
      });
    },
    [patchFestival, setValue],
  );

  const handleLocationNameChange = useCallback(
    (nextLocationName) => {
      setValue('locationName', nextLocationName, { shouldDirty: true });
      patchFestival((current) => ({
        location: {
          ...(current.location ?? {}),
          locationName: nextLocationName,
        },
      }));
      if (isLocationNameSet(nextLocationName)) {
        setWorkspaceErrors((prev) => {
          const next = { ...prev };
          delete next.locationName;
          return next;
        });
      }
    },
    [patchFestival, setValue],
  );

  const persistFestival = useCallback(
    async (values, { autosave = false } = {}) => {
      const currentFestival = festivalRef.current;
      const nextWorkspaceErrors = computeWorkspaceErrors(
        currentFestival,
        values.locationName,
      );
      if (Object.keys(nextWorkspaceErrors).length > 0) {
        setWorkspaceErrors(nextWorkspaceErrors);
        if (!autosave) {
          requestAnimationFrame(() => {
            scrollToFirstErrorRef.current({}, nextWorkspaceErrors);
          });
        }
        return null;
      }
      setWorkspaceErrors({});

      const updatedFestival = {
        ...currentFestival,
        name: values.name.trim(),
        description: values.description.trim(),
        links: values.links,
        dates: withServerDateFieldsList(
          getCompleteDates(currentFestival.dates),
        ),
        location: buildFestivalLocationPayload(
          currentFestival.location,
          values.locationName,
          currentFestival.orgId,
        ),
        _autosave: autosave,
      };

      delete updatedFestival.startDate;

      const wasNewFestival = !currentFestival.festivalId;
      const saved = await saveMutation.mutateAsync(updatedFestival);

      if (wasNewFestival && saved?.festivalId) {
        onNewFestivalSaved?.(saved);
        history.push(`/festival/${saved.festivalId}/view`);
      }

      return saved;
    },
    [history, onNewFestivalSaved, saveMutation],
  );

  const onSubmit = async (values) => {
    await persistFestival(values, { autosave: false });
  };

  const onFileUpload = useCallback(
    (fileInfo) => {
      const { filename, url } = fileInfo;
      patchFestival((current) => ({
        ...current,
        photoURL: url,
        photo: filename,
      }));
    },
    [patchFestival],
  );

  const onFileDelete = useCallback(() => {
    patchFestival((current) => ({
      ...current,
      photoURL: null,
      photo: null,
    }));
  }, [patchFestival]);

  const handleStatusChange = async (status) => {
    const current = festival.status || 'UNPUBLISHED';
    if (!festival.festivalId || status === current) {
      return;
    }
    patchFestival({ ...festival, status });
    try {
      await statusMutation.mutateAsync({
        festivalId: festival.festivalId,
        status,
      });
    } catch {
      patchFestival({ ...festival, status: current });
    }
  };

  const onInvalid = useCallback(
    (rhfErrors) => {
      const nextWorkspaceErrors = computeWorkspaceErrors(
        festival,
        getValues('locationName'),
      );
      setWorkspaceErrors(nextWorkspaceErrors);
      requestAnimationFrame(() => {
        scrollToFirstError(rhfErrors, nextWorkspaceErrors);
      });
    },
    [festival, getValues, scrollToFirstError],
  );

  const submitForm = handleSubmit(onSubmit, onInvalid);
  const persistFestivalRef = useRef(persistFestival);
  persistFestivalRef.current = persistFestival;
  const getValuesRef = useRef(getValues);
  getValuesRef.current = getValues;
  const isEditAutosaveRef = useRef(isEditAutosave);
  isEditAutosaveRef.current = isEditAutosave;

  const flushPendingAutosave = useCallback(() => {
    if (
      !isEditAutosaveRef.current ||
      skipAutosaveRef.current ||
      saveInFlightRef.current
    ) {
      return;
    }

    const values = getValuesRef.current();
    const currentFestival = festivalRef.current;
    const pendingSnapshot = buildAutosaveSnapshot(values, currentFestival);
    if (pendingSnapshot === lastSavedSnapshotRef.current) {
      return;
    }

    const nextWorkspaceErrors = computeWorkspaceErrors(
      currentFestival,
      values.locationName,
    );
    if (Object.keys(nextWorkspaceErrors).length > 0) {
      setWorkspaceErrors(nextWorkspaceErrors);
      return;
    }

    if (!values.name?.trim() || !values.description?.trim()) {
      return;
    }

    saveInFlightRef.current = true;
    persistFestivalRef
      .current(values, { autosave: true })
      .then((saved) => {
        if (saved) {
          lastSavedSnapshotRef.current = pendingSnapshot;
        }
      })
      .finally(() => {
        saveInFlightRef.current = false;
      });
  }, []);

  useEffect(() => {
    if (!isEditAutosave || skipAutosaveRef.current) {
      return undefined;
    }

    const watchedFormValues = {
      name: watchedName,
      description: watchedDescription,
      locationName: watchedLocationName,
      links: watchedLinks,
    };
    const snapshot = buildAutosaveSnapshot(watchedFormValues, festival);
    if (snapshot === lastSavedSnapshotRef.current) {
      return undefined;
    }

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = setTimeout(() => {
      flushPendingAutosave();
    }, EDIT_AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [
    festival.dates,
    festival.location,
    festival.photo,
    festival.photoURL,
    flushPendingAutosave,
    isEditAutosave,
    watchedDescription,
    watchedLinks,
    watchedLocationName,
    watchedName,
  ]);

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      flushPendingAutosave();
    };
  }, [flushPendingAutosave]);

  const showLoading = bridgeLoading || loading || isSubmitting;
  const dates =
    festival.dates?.length > 0 ? festival.dates : [defaultDateObject];
  const headerTitle =
    festival?.name?.trim() || watchedName?.trim() || 'Add New Event';
  const currentStatus = festival.status || 'UNPUBLISHED';
  const showManualSave = !readOnly && !isEditAutosave;
  const showStatusAndQr = Boolean(festival.festivalId);

  const fileList = [];
  if (festival.photoURL) {
    fileList.push({
      uid: '-1',
      name: festival.photo,
      url: festival.photoURL,
      status: 'done',
    });
  }

  if (showLoading && bridgeLoading) {
    return <Loading />;
  }

  const eventImageCard = (
    <section className="bethere-form-section">
      <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-[#121212]">
        <FontAwesomeIcon
          icon={['fas', 'image']}
          className="text-[var(--bethere-primary)]"
        />
        Event Image
      </h2>
      <FileUpload
        variant="hero"
        fullWidth={true}
        fileList={fileList}
        onFileUpload={onFileUpload}
        onFileDelete={onFileDelete}
        readOnly={readOnly}
      />
    </section>
  );

  const basicDetailsCard = (
    <section className="bethere-form-section">
      <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-[#121212]">
        <FontAwesomeIcon
          icon={['fas', 'circle-info']}
          className="text-[var(--bethere-primary)]"
        />
        Basic Details
      </h2>
      <FormRow label="Event Name" layout="stacked" error={errors.name?.message}>
        <input
          type="text"
          placeholder="Type an event name here..."
          {...register('name', { required: 'Name is required' })}
        />
      </FormRow>
      <DateFormWidget
        label="Event Dates & Times"
        layout="stacked"
        dates={dates}
        handleDateChange={handleDateChange}
        allowMultiple={true}
        readOnly={readOnly}
        datesError={workspaceErrors.dates}
      />
      <FormRow
        label="General Information"
        layout="stacked"
        error={errors.description?.message}
      >
        <textarea
          placeholder="Festival Information..."
          {...register('description', {
            required: 'Description is required',
          })}
        />
      </FormRow>
      <FormRow
        label={
          <span className="inline-flex items-center gap-2">
            <FontAwesomeIcon icon={['fas', 'globe']} />
            Website
          </span>
        }
        layout="stacked"
      >
        <input
          type="text"
          placeholder="https://example.com"
          {...register('links.web')}
        />
      </FormRow>
      <FormRow
        label={
          <span className="inline-flex items-center gap-2">
            <FontAwesomeIcon icon={['fab', 'facebook']} />
            Facebook
          </span>
        }
        layout="stacked"
      >
        <input
          type="text"
          placeholder="https://facebook.com/page"
          {...register('links.facebook')}
        />
      </FormRow>
      <FormRow
        label={
          <span className="inline-flex items-center gap-2">
            <FontAwesomeIcon icon={['fab', 'x-twitter']} />X (Twitter)
          </span>
        }
        layout="stacked"
      >
        <input
          type="text"
          placeholder="https://x.com/handle"
          {...register('links.twitter')}
        />
      </FormRow>
      <FormRow
        label={
          <span className="inline-flex items-center gap-2">
            <FontAwesomeIcon icon={['fab', 'instagram']} />
            Instagram
          </span>
        }
        layout="stacked"
      >
        <input
          type="text"
          placeholder="https://instagram.com/profile"
          {...register('links.instagram')}
        />
      </FormRow>
    </section>
  );

  const eventMapCard = (
    <section className="bethere-form-section">
      <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-[#121212]">
        <FontAwesomeIcon
          icon={['fas', 'map-location-dot']}
          className="text-[var(--bethere-primary)]"
        />
        Event Map
      </h2>
      <FormRow
        label="Location"
        layout="stacked"
        formField="location"
        error={workspaceErrors.location || workspaceErrors.locationName}
      >
        <LocationMapField
          location={festival.location}
          locationName={watchedLocationName}
          onLocationChange={readOnly ? undefined : handleLocationChange}
          onLocationNameChange={readOnly ? undefined : handleLocationNameChange}
          readOnly={readOnly}
        />
      </FormRow>
    </section>
  );

  return (
    <div>
      <FestivalHeader
        title={headerTitle}
        onSave={showManualSave ? submitForm : undefined}
        showSave={showManualSave}
        showExit={true}
        editHref={
          readOnly && festival.festivalId
            ? `/festival/${festival.festivalId}`
            : undefined
        }
      />
      <form
        ref={formRef}
        className="admin-form"
        onSubmit={
          showManualSave ? submitForm : (event) => event.preventDefault()
        }
        noValidate
      >
        {showStatusAndQr ? (
          <div className="edit-event-shell">
            <div className="edit-event-layout">
              <fieldset
                disabled={readOnly}
                className="edit-event-main m-0 min-w-0 border-0 p-0"
              >
                {eventImageCard}
                {basicDetailsCard}
                {eventMapCard}
                {festival.festivalId && !readOnly ? (
                  <FestivalRelatedContent festivalId={festival.festivalId} />
                ) : null}
                {showManualSave && (
                  <div className="flex justify-end border-t border-black/[0.08] pt-6 pb-2">
                    <Button
                      type="submit"
                      disabled={isSubmitting || saveMutation.isPending}
                      mode="dark"
                    >
                      Save Festival
                    </Button>
                  </div>
                )}
              </fieldset>
              <aside className="edit-event-sidebar">
                <FestivalEventStatusPanel
                  festivalId={festival.festivalId}
                  festivalName={
                    watchedName?.trim() || festival.name || 'this event'
                  }
                  status={currentStatus}
                  readOnly={readOnly}
                  isPending={statusMutation.isPending}
                  onStatusChange={handleStatusChange}
                />
                <FestivalQrCodesSidebar
                  festivalId={festival.festivalId}
                  status={currentStatus}
                />
              </aside>
            </div>
          </div>
        ) : (
          <fieldset disabled={readOnly} className="m-0 min-w-0 border-0 p-0">
            <div className="glass-panel p-6 sm:p-8">
              {eventImageCard}
              {basicDetailsCard}
              {eventMapCard}
              {showManualSave && (
                <div className="flex justify-end border-t border-black/[0.08] pt-6 pb-2">
                  <Button
                    type="submit"
                    disabled={isSubmitting || saveMutation.isPending}
                    mode="dark"
                  >
                    Save Festival
                  </Button>
                </div>
              )}
            </div>
          </fieldset>
        )}
      </form>
    </div>
  );
}

export { FestivalFormFields };

export function FestivalForm(props) {
  const festival = useFestivalWorkspaceStore((s) => s.festival);
  const setFestival = useFestivalWorkspaceStore((s) => s.setFestival);
  const patchFestival = useFestivalWorkspaceStore((s) => s.patchFestival);
  const saveMutation = useSaveFestivalMutation();
  const statusMutation = useChangeFestivalStatusMutation();
  const currentUser = useAuthStore((s) => s.currentUser);
  const canModify = canModifyContent(currentUser);

  const id = props.match?.params?.id;
  const numericId =
    id !== undefined && id !== '' && !Number.isNaN(Number(id))
      ? String(id)
      : undefined;
  const isEditRoute =
    Boolean(numericId) &&
    props.match?.path != null &&
    !props.match.path.endsWith('/view');

  const readOnly =
    props.readOnly === true ||
    !canModify ||
    (props.match?.path != null && props.match.path.endsWith('/view'));

  const q = useFestivalDetailQuery(numericId);

  useEffect(() => {
    if (!props.match || !numericId) {
      setFestival({});
    }
  }, [props.match, numericId, setFestival]);

  useEffect(() => {
    if (q.isSuccess && q.data) {
      setFestival(q.data);
    }
  }, [q.isSuccess, q.data, setFestival]);

  if (isEditRoute && !canModify) {
    return <Redirect to={`/festival/${numericId}/view`} />;
  }

  const bridgeLoading = Boolean(numericId && q.isPending);
  const loading = saveMutation.isPending || statusMutation.isPending;

  return (
    <FestivalFormFields
      festival={festival}
      patchFestival={patchFestival}
      saveMutation={saveMutation}
      statusMutation={statusMutation}
      loading={loading}
      bridgeLoading={bridgeLoading}
      readOnly={readOnly}
      onNewFestivalSaved={props.onNewFestivalSaved}
    />
  );
}
